import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch } from '../../lib/api-client';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

interface SubSellerItem {
  id: string;
  parentId: string;
  name: string;
  payinSharePercent: number;
  payoutSharePercent: number;
  level: number;
  status: string;
}

export function SubSellerEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { subSellerId } = useParams<{ subSellerId: string }>();

  const [name, setName] = useState('');
  const [payinSharePercent, setPayinSharePercent] = useState('');
  const [payoutSharePercent, setPayoutSharePercent] = useState('');
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const { data } = useQuery({
    queryKey: ['seller', 'sub-sellers'],
    queryFn: () => apiGet<{ items: SubSellerItem[]; total: number }>('/seller/sub-sellers'),
  });

  const subSeller = data?.ok
    ? data.data.items.find((ss) => ss.id === subSellerId)
    : undefined;

  useEffect(() => {
    if (subSeller && !loaded) {
      setName(subSeller.name);
      setPayinSharePercent(String(subSeller.payinSharePercent));
      setPayoutSharePercent(String(subSeller.payoutSharePercent));
      setLoaded(true);
    }
  }, [subSeller, loaded]);

  const updateMutation = useMutation({
    mutationFn: () =>
      apiPatch(`/seller/sub-sellers/${subSellerId}`, {
        name,
        payinSharePercent: parseFloat(payinSharePercent) || 0,
        payoutSharePercent: parseFloat(payoutSharePercent) || 0,
      }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'sub-sellers'] });
        navigate('/seller/sub-sellers');
      } else {
        setError(res.error.message);
      }
    },
    onError: (err) => {
      setError((err as Error).message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    updateMutation.mutate();
  };

  if (!subSeller && data?.ok) {
    return (
      <div className="p-6 text-red-600">{t('common.error')}</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/seller/sub-sellers')}>
          &larr; {t('seller.subSellersTitle')}
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('common.edit')} - {subSeller?.name ?? ''}
        </h1>
      </div>

      <Card className="max-w-xl">
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
                {error}
              </div>
            )}

            <Input
              label={t('sellers.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <Input
              label={`${t('sellers.payinFee')} (%)`}
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={payinSharePercent}
              onChange={(e) => setPayinSharePercent(e.target.value)}
              required
            />

            <Input
              label={`${t('sellers.payoutFee')} (%)`}
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={payoutSharePercent}
              onChange={(e) => setPayoutSharePercent(e.target.value)}
              required
            />

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? t('common.loading') : t('common.save')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/seller/sub-sellers')}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
